import React, { createContext, useContext, useState, useCallback } from 'react';
import type {
  HIDFlashProgrammer,
  ProgrammerInfo,
  ChipDefinition,
  DeviceIdResult,
  ProgrammerStatus,
} from '@/types/hid8051';
import { NuLinkDevice } from './nulink/NuLinkDevice';
import { SnLinkDevice } from './snlink/SnLinkDevice';

// ============================================================================
// Programmer Context — React context for 8051 HID programmer state
// ============================================================================

interface ProgrammerState {
  programmer: HIDFlashProgrammer | null;
  programmerInfo: ProgrammerInfo | null;
  targetInfo: DeviceIdResult | null;
  selectedChip: ChipDefinition | null;
  status: ProgrammerStatus;
  error: string | null;
  connectNuLink: () => Promise<void>;
  connectSnLink: () => Promise<void>;
  disconnect: () => Promise<void>;
  selectChip: (chip: ChipDefinition | null) => void;
  connectTarget: () => Promise<void>;
}

const ProgrammerContext = createContext<ProgrammerState | null>(null);

export const ProgrammerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [programmer, setProgrammer] = useState<HIDFlashProgrammer | null>(null);
  const [programmerInfo, setProgrammerInfo] = useState<ProgrammerInfo | null>(null);
  const [targetInfo, setTargetInfo] = useState<DeviceIdResult | null>(null);
  const [selectedChip, setSelectedChip] = useState<ChipDefinition | null>(null);
  const [status, setStatus] = useState<ProgrammerStatus>('disconnected');
  const [error, setError] = useState<string | null>(null);

  const connectNuLink = useCallback(async () => {
    setStatus('busy');
    setError(null);
    try {
      const device = await NuLinkDevice.requestDevice();
      const info = await device.connect();
      setProgrammer(device);
      setProgrammerInfo(info);
      setStatus('connected');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to connect Nu-Link');
      setStatus('error');
      setProgrammer(null);
      setProgrammerInfo(null);
    }
  }, []);

  const connectSnLink = useCallback(async () => {
    setStatus('busy');
    setError(null);
    try {
      const device = await SnLinkDevice.requestDevice();
      const info = await device.connect();
      setProgrammer(device);
      setProgrammerInfo(info);
      setStatus('connected');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to connect SN-Link');
      setStatus('error');
      setProgrammer(null);
      setProgrammerInfo(null);
    }
  }, []);

  const disconnect = useCallback(async () => {
    if (programmer) {
      await programmer.disconnect();
    }
    setProgrammer(null);
    setProgrammerInfo(null);
    setTargetInfo(null);
    setSelectedChip(null);
    setStatus('disconnected');
    setError(null);
  }, [programmer]);

  const selectChip = useCallback((chip: ChipDefinition | null) => {
    setSelectedChip(chip);
    setTargetInfo(null);
  }, []);

  const connectTarget = useCallback(async () => {
    if (!programmer || !selectedChip) {
      setError('No programmer or chip selected');
      return;
    }
    setStatus('busy');
    setError(null);
    try {
      await programmer.setConfig(selectedChip);
      const devId = await programmer.readDeviceId();
      setTargetInfo(devId);
      setStatus('connected');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to connect to target');
      setStatus('error');
      setTargetInfo(null);
    }
  }, [programmer, selectedChip]);

  return (
    <ProgrammerContext.Provider
      value={{
        programmer,
        programmerInfo,
        targetInfo,
        selectedChip,
        status,
        error,
        connectNuLink,
        connectSnLink,
        disconnect,
        selectChip,
        connectTarget,
      }}
    >
      {children}
    </ProgrammerContext.Provider>
  );
};

export function useProgrammerContext(): ProgrammerState {
  const ctx = useContext(ProgrammerContext);
  if (!ctx) {
    throw new Error('useProgrammerContext must be used within a ProgrammerProvider');
  }
  return ctx;
}
